import type { JournalData } from "@qendo/profile/journal";
import type { GroupedOptionData } from "@qendo/types";
import type { TimeZone } from "@qendo/util";
import type { DateArg } from "date-fns";
import type { Frequency } from "expo-calendar";
import type { QendoIconSet } from "~/components/QendoIcon";
import type { Numeric } from "~/util/string";

export enum EventCategory {
    appointment = "appointment",
    home = "home",
    social = "social",
    exercise = "exercise",
}

export const EventCategories = Object.values(EventCategory);

export type CalenderEventOptionData = GroupedOptionData<EventCategory>;

export enum CalendarFrequency {
    DAILY = "daily",
    WEEKLY = "weekly",
    MONTHLY = "monthly",
    FORTNIGHTLY = "fortnightly",
    YEARLY = "yearly",
}

export type ResolvedFrequency<T extends CalendarFrequency | Frequency = Frequency> =
    T extends CalendarFrequency.FORTNIGHTLY ? Frequency.WEEKLY : T & Frequency;

export const FrequencyPluralMap: Record<CalendarFrequency, string> = {
    [CalendarFrequency.YEARLY]: "years",
    [CalendarFrequency.MONTHLY]: "months",
    [CalendarFrequency.WEEKLY]: "weeks",
    [CalendarFrequency.FORTNIGHTLY]: "fortnights",
    [CalendarFrequency.DAILY]: "days",
};

export const EventCategoryMap: Record<EventCategory, string> = {
    [EventCategory.appointment]: "Appointments",
    [EventCategory.home]: "At Home",
    [EventCategory.social]: "Social/Work",
    [EventCategory.exercise]: "Exercise",
};

export const EventCategoryIconMap: Record<EventCategory, QendoIconSet> = {
    [EventCategory.appointment]: "calendar",
    [EventCategory.home]: "home",
    [EventCategory.social]: "work",
    [EventCategory.exercise]: "exercise",
};

export type EventTypeData = {
    title: string;
    group: EventCategory;
    id: string;
};

export type RecurrenceData = {
    frequency: CalendarFrequency;
    interval: number;
    occurrence?: number | null;
    end_date?: (string & DateArg<Date>) | null;
};

export type SingleEventData = {
    recurrence?: null;
    multi_day: false;
    occurrence?: null;
    primary_id?: null;
};

export type RecurringEventData = {
    recurrence: RecurrenceData;
    multi_day: boolean;
    occurrence?: null;
    primary_id?: null;
};

export type MultiDayEventData = {
    recurrence?: null;
    end_at: string & DateArg<Date>;
    multi_day: true;
    occurrence?: null;
    primary_id?: null;
};

export type RepeatEntryData<Primary extends string, Day extends number> = {
    id: `${Primary}[${Day}]`;
    recurrence?: null;
    multi_day: boolean;
    occurrence: Day;
    primary_id: Primary;
};

export type DayEntryData = SingleEventData | RecurringEventData | MultiDayEventData | RepeatEntryData<string, number>;

export type CalendarEventData = {
    id: string;
    title: string;
    option_id: string;
    category: EventCategory;
    notes?: string | null;
    location?: string | null;
    recurrence?: RecurrenceData | null;
    start_at: string & DateArg<Date>;
    all_day: boolean;
    end_at?: (string & DateArg<Date>) | null;
    time_zone?: TimeZone | null;
    photograph_ids?: string[];
    multi_day: boolean;
    occurrence?: number | null;
    primary_id?: string | null;
    search: string[];
} & DayEntryData;

export type RecurrencePayload = {
    frequency: CalendarFrequency;
    interval: Numeric;
    times?: Numeric | null;
    repeat_until?: (string & DateArg<Date>) | null;
};
export type SingleEventPayload = {
    frequency?: null;
    interval?: null;
    times?: null;
    repeat_until?: null;
};

export type EventTimingPayload = RecurrencePayload | SingleEventPayload;
export type CalendarCreatePayload = {
    id?: null;
    option_id: string;
    title: string;
    notes?: string | null;
    location?: string | null;
    start_at: string & DateArg<Date>;
    all_day: boolean;
    end_at?: (string & DateArg<Date>) | null;
    time_zone?: string | null;
} & EventTimingPayload;
export type CalendarUpdatePayload = {
    id: string;
} & Omit<CalendarCreatePayload, "id">;
export type CalendarPayload = CalendarCreatePayload | CalendarUpdatePayload;

export type CalendarEntry = JournalData | CalendarEventData;
export type CalendarList<T extends CalendarEntry = CalendarEntry> = T[];
export type CalendarRecord<T extends CalendarEntry = CalendarEntry> = Record<string & DateArg<Date>, CalendarList<T>>;
export type CalendarListFilter<T extends CalendarEntry = CalendarEntry> = (
    date: DateArg<Date>,
    search?: string | null | undefined,
) => CalendarList<T>;
export type CalendarGroupFilter<T extends CalendarEntry = CalendarEntry> = (
    date: DateArg<Date>,
    search?: string | null | undefined,
) => CalendarRecord<T>;
